import { useId, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { DEMO_SCA_CODE, verifySca } from '@/mocks/api';
import { useMutation } from '@/hooks/useAsync';
import { useSession } from '@/state/session';
import { errorMessage, isMockApiError } from '@/mocks/errors';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';

/**
 * Strong customer authentication challenge.
 *
 * TRACE:XS-SMEM-WR-016 — a documented SCA challenge method gates login,
 * payments and card-data reveal. Wrong codes are counted and the challenge
 * expires, so an attempt limit and a timeout path both exist.
 */
export function ScaPage() {
  const navigate = useNavigate();
  const phase = useSession((s) => s.phase);
  const challengeId = useSession((s) => s.pendingChallengeId);
  const returnTo = useSession((s) => s.returnTo);
  const completeSca = useSession((s) => s.completeSca);
  const signOut = useSession((s) => s.signOut);

  // Pre-entered so the flow continues in one tap. The challenge, the attempt
  // limit and the expiry are all still enforced — clear it to test a wrong code.
  const [code, setCode] = useState(DEMO_SCA_CODE);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const codeId = useId();
  const codeRef = useRef<HTMLInputElement | null>(null);
  const alertRef = useRef<HTMLDivElement | null>(null);

  const mutation = useMutation(async (id: string, value: string) => verifySca(id, value));

  // Guard on the session phase, not on the challenge id. `completeSca()`
  // clears the challenge, so a challenge-based guard would re-render and
  // bounce the user to /mobile/login before the forward navigation lands.
  if (phase === 'awaiting-company') return <Navigate to="/mobile/select-company" replace />;
  if (phase === 'ready') return <Navigate to="/mobile/home" replace />;
  if (phase !== 'awaiting-sca' || !challengeId) return <Navigate to="/mobile/login" replace />;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFieldError(null);
    setSummaryError(null);

    const outcome = await mutation.run(challengeId, code);
    if (!outcome.ok) {
      if (outcome.error === null) return; // duplicate submit
      const err = outcome.error;
      if (isMockApiError(err) && err.fieldErrors?.['code']) {
        setFieldError(err.fieldErrors['code']);
        setCode('');
        codeRef.current?.focus();
      } else {
        setSummaryError(errorMessage(err));
        requestAnimationFrame(() => alertRef.current?.focus());
      }
      return;
    }
    completeSca();
    const next = useSession.getState().phase;
    if (next === 'awaiting-company') navigate('/mobile/select-company');
    else navigate(returnTo && returnTo.startsWith('/mobile/') ? returnTo : '/mobile/home');
  };

  const topError = summaryError;

  return (
    <>
      <MobileHeader
        title="Security check"
        backTo="/mobile/login"
        backLabel="sign in"
      />
      <div className="tm-stack" style={{ paddingBlockStart: 20 }}>
        <div>
          {/* The header supplies the single <h1> for this route, so the
              prominent in-page title is an <h2>. */}
          <h2 className="tm-page-title">Confirm it is you</h2>
          <p className="tm-muted" style={{ marginBlockStart: 6 }}>
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>

        {topError ? (
          <div className="tm-error-summary" role="alert" tabIndex={-1} ref={alertRef}>
            <strong>There is a problem</strong>
            <p style={{ margin: '4px 0 0' }}>{topError}</p>
            <button
              type="button"
              className="tm-btn tm-btn--secondary tm-btn--sm"
              style={{ marginBlockStart: 10 }}
              onClick={() => {
                signOut();
                navigate('/mobile/login');
              }}
            >
              Start again
            </button>
          </div>
        ) : null}

        <form className="tm-stack" onSubmit={(e) => void submit(e)} noValidate>
          <div className="tm-field">
            <label className="tm-label" htmlFor={codeId}>
              Authentication code
            </label>
            <input
              id={codeId}
              ref={codeRef}
              className="tm-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? `${codeId}-error` : `${codeId}-hint`}
              style={{ letterSpacing: '0.4em', fontVariantNumeric: 'tabular-nums' }}
            />
            <div className="tm-field__error-slot">
              {fieldError ? (
                <span className="tm-field__error" id={`${codeId}-error`}>
                  {fieldError}
                </span>
              ) : (
                <span className="tm-hint" id={`${codeId}-hint`}>
                  Demo code: 481920
                </span>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="tm-btn tm-btn--block"
            disabled={mutation.pending || code.length !== 6}
          >
            {mutation.pending ? 'Checking…' : 'Confirm'}
          </button>
        </form>
      </div>
    </>
  );
}
